import React from "react";
import PropTypes from "prop-types";
import {createPortal} from "react-dom";
import {useTheme} from "../../theme/ThemeProvider";
import {buildStyles} from "../buildStyles";
import Container from "../layout/Container";
import FlexContainer from "../layout/FlexContainer";
import Text from "../display/Text";

const useStyles = (theme) =>
  buildStyles({
    tooltip: {
      position: "fixed",
      backgroundColor: theme.colors.white,
      border: `1px solid ${theme.colors.gray200}`,
      borderRadius: `${theme.radius.md}px`,
      padding: 0,
      boxShadow: theme.shadows.md,
      zIndex: theme.zIndex.tooltip + 200,
      transform: "translate(-50%, -100%)",
      marginTop: theme.spacing(-1),
      minWidth: "120px",
      pointerEvents: "none",
      overflow: "hidden",
    },
    coloredBorder: {
      width: "4px",
      height: "100%",
      flexShrink: 0,
    },
    content: {
      padding: theme.spacing(2, 3),
      flex: 1,
    },
  });

/**
 * BaseChartTooltip - Unified tooltip component for all chart types
 *
 * Usage patterns:
 *
 * 1. Standard items pattern (recommended):
 *    <BaseChartTooltip
 *      position={position}
 *      color="#ff0000"
 *      items={[
 *        {label: "SMS", value: "45% - 123 messages", color: "#2f7afb"},
 *        {label: "WhatsApp", value: "35% - 89 messages", color: "#25D366"}
 *      ]}
 *    />
 *
 * 2. Single item:
 *    <BaseChartTooltip
 *      position={position}
 *      color="#00ff00"
 *      items={[
 *        {label: "Satisfied", value: "142 guests", color: "#00ff00"}
 *      ]}
 *    />
 *
 * 3. Custom mode:
 *    <BaseChartTooltip position={position} color="#ff0000">
 *      <CustomContent />
 *    </BaseChartTooltip>
 */
const BaseChartTooltip = ({
  position,
  color,
  items = [],
  children,
  chartContainerRef,
  ...props
}) => {
  const {theme} = useTheme();
  const classes = useStyles(theme);

  if (!position) return null;

  // Convert chart-relative position to viewport
  const getViewportPosition = () => {
    if (!chartContainerRef?.current) {
      return position;
    }

    const containerRect = chartContainerRef.current.getBoundingClientRect();
    return {
      x: containerRect.left + position.x,
      y: containerRect.top + position.y,
    };
  };

  const viewportPosition = getViewportPosition();

  if (children) {
    return createPortal(
      <Container
        className={classes.tooltip}
        sx={{
          left: viewportPosition.x,
          top: viewportPosition.y - 10,
        }}
        {...props}
      >
        <FlexContainer alignItems="stretch" gap={"0px"}>
          {color && (
            <Container
              className={classes.coloredBorder}
              sx={{
                backgroundColor: color,
              }}
            />
          )}
          <Container className={classes.content}>{children}</Container>
        </FlexContainer>
      </Container>,
      document.body,
    );
  }

  return createPortal(
    <Container
      className={classes.tooltip}
      sx={{
        left: viewportPosition.x,
        top: viewportPosition.y - 10,
      }}
      {...props}
    >
      <FlexContainer alignItems="stretch" gap={"0px"}>
        {color && (
          <Container
            className={classes.coloredBorder}
            sx={{
              backgroundColor: color,
            }}
          />
        )}
        <Container className={classes.content}>
          <FlexContainer flexDirection="column" gap={theme.spacing(1.5)}>
            {items.map((item, index) => (
              <FlexContainer
                key={index}
                alignItems="flex-start"
                gap={theme.spacing(1.5)}
              >
                <Container
                  sx={{
                    width: "3px",
                    height: "32px",
                    backgroundColor: item.color || theme.colors.gray400,
                    borderRadius: `${theme.radius.sm}px`,
                    flexShrink: 0,
                    marginTop: theme.spacing(0.5),
                  }}
                />
                <FlexContainer flexDirection="column" gap={theme.spacing(0.5)}>
                  <Text variant="textSM" weight="semibold" color="textPrimary">
                    {item.label}
                  </Text>
                  <Text variant="textXS" color="textSecondary">
                    {item.value}
                  </Text>
                </FlexContainer>
              </FlexContainer>
            ))}
          </FlexContainer>
        </Container>
      </FlexContainer>
    </Container>,
    document.body,
  );
};

BaseChartTooltip.propTypes = {
  position: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
  }),
  color: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      color: PropTypes.string,
    }),
  ),
  children: PropTypes.node,
  chartContainerRef: PropTypes.object,
};

export default BaseChartTooltip;
